import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { findNextScrollableParent } from '@components/component-helpers';
import { DataGrid } from '@components/data-grid';
import { EmptyState } from '@components/empty-state';
import { IconApplicants } from '@components/icons/applicants';
import { IconEdit } from '@components/icons/edit';
import { IconSend } from '@components/icons/send';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { IconTeam } from '@components/icons/team';
import { IconTrash } from '@components/icons/trash';
import { Pagination, usePagination } from '@components/pagination';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { storage } from '@utils/browser';
import { sortByObjectKey } from '@utils/collections';

import { Box } from 'components/Box';
import { Quickfilter } from 'components/Quickfilter';
import { useAuthenticatedUser } from 'features/authentication';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

export const getUserTabs = (userStore, t) => {
  return [
    {
      list: userStore.sorted,
      label: t('user.tabs.all'),
    },
    {
      list: userStore.superadmins,
      label: t('user.tabs.superadmins'),
    },
    {
      list: userStore.admins,
      label: t('user.tabs.admins'),
    },
    {
      list: userStore.recruiters,
      label: t('user.tabs.recruiters'),
    },
    {
      list: userStore.limited,
      label: t('user.tabs.limited'),
    },
    {
      list: userStore.employees,
      label: t('user.tabs.employees'),
    },
  ];
};

export const TeamList = observer(() => {
  const { userStore, t } = useStore();

  const [settings, setSettings] = useState(() => {
    const defaultSettings = getDefaultSettings();
    const stored = storage.get('team-settings');
    if (!stored) return defaultSettings;
    return { ...defaultSettings, ...JSON.parse(stored) };
  });

  const [sort, setSort] = useState(() => {
    const defaultSort = getDefaultSort();
    const stored = storage.get('team-sort');
    if (!stored) return defaultSort;
    return { ...defaultSort, ...JSON.parse(stored) };
  });

  useEffect(() => {
    storage.set('team-settings', JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    storage.set('team-sort', JSON.stringify(sort));
  }, [sort]);

  let props = {
    settings,
    onSettingsChange: setSettings,
    sort,
    onSortChange: setSort,
  };

  const storedTabIndex = storage.get('team-tab');

  return (
    <Quickfilter
      onChange={(idx) => storage.set('team-tab', idx)}
      defaultIndex={storedTabIndex ? Number(storedTabIndex) : 0}
      items={getUserTabs(userStore, t).map((tab) => ({
        count: tab.list.length,
        label: tab.label,
        render: () => <Panel users={tab.list} {...props} />,
      }))}
    />
  );
});

const Panel = observer(({ users, ...rest }) => {
  const { t } = useStore();

  if (users.length === 0) {
    return (
      <EmptyState
        header={t('user.list.empty.header')}
        content={t('user.list.empty.content')}
        icon={<IconTeam size={80} />}
      />
    );
  }

  return <TeamSection users={users} {...rest} />;
});

const TeamSection = observer(({ users, sort, settings, onSortChange, onSettingsChange }) => {
  const { current, t } = useStore();
  const { showModal } = useModal();
  const navigate = useNavigate();
  const contentRef = useRef();
  const me = useAuthenticatedUser();

  const sortedUsers = useMemo(() => {
    let sortKey = sort.key;
    return sortByObjectKey(users, sortKey, sort.order === 'descending');
  }, [users, sort]);

  const { numPages, selectedPage, selectPage, getItemsForPage } = usePagination({
    pageSize: 50,
    defaultPage: Number(storage.get('team-page')) || 1,
    count: sortedUsers.length,
  });

  useEffect(() => {
    storage.set('team-page', selectedPage);
  }, [selectedPage]);

  const items = getItemsForPage(sortedUsers);

  function onSort(newSort) {
    onSortChange(newSort);
    selectPage(1);
  }

  function editUserModal(user) {
    showModal('EDIT_USER', {
      _container: {
        closeOnOverlayClickOrEsc: false,
        width: '460px',
      },
      user,
    });
  }

  function deleteUserModal(user) {
    showModal('COMMON_REMOVE_CONFIRM', {
      _container: {
        width: '400px',
      },
      data: { id: user.id, confirmText: t('modals.removeconfirm.user.confirm') },
      confirm: user.delete,
    });
  }

  function sendInviteModal(user) {
    showModal('CONFIRM_MODAL', {
      _container: {
        width: '460px',
      },
      confirm: user.sendInvite,
      data: { id: user.id },
      buttonIcon: <IconSend />,
      headerText: t('modals.sendinvite.header'),
      mainText: t('modals.sendinvite.main', user.name),
      confirmText: t('modals.sendinvite.button'),
    });
  }

  const [columns] = useState(() => {
    let columns = [
      {
        label: t('userlist.header.name'),
        key: 'name',
        type: 'text-long',
        sortable: true,
        render: (user) => <RenderUserInfo user={user} />,
      },
      {
        label: t('userlist.header.email'),
        type: 'text-long',
        key: 'email',
        sortable: true,
      },
      {
        label: t('userlist.header.role'),
        key: 'roleDisplay',
        type: 'text-short',
        sortable: true,
      },
    ];
    if (current.info.isEnterprise) {
      columns.push({
        label: t('userlist.header.department'),
        key: 'departmentsDisplay',
        type: 'text-long',
        render: (user) => <RenderDepartments user={user} />,
      });
    }

    if (me.role === 'admin' || me.role === 'superadmin') {
      columns.push({
        label: t('userlist.header.access'),
        type: 'text-short',
        key: 'access',
        render: (user) => <RenderUserAccess user={user} />,
      });

      columns.push({
        label: t('userlist.header.status'),
        key: 'status',
        type: 'text-short',
        sortable: true,
        render: (user) => (
          <UserStatus status={user.status}>
            <IconStatusIndicator />
            <div>{t(`user.status.${user.status}`)}</div>
          </UserStatus>
        ),
      });
    }
    return columns;
  });

  useLayoutEffect(() => {
    const parentNode = findNextScrollableParent(contentRef.current);
    parentNode?.scrollTo(0, 0);
  }, [selectedPage]);

  return (
    <div ref={contentRef}>
      <DataGrid
        columns={columns}
        data={items}
        columnSettings={settings}
        onColumnSettingsChange={onSettingsChange}
        sort={sort}
        onSortChange={onSort}
        onRowClick={{
          label: t('user.options.view'),
          icon: <IconApplicants />,
          callback: (user) => {
            navigate(user.profileUrl);
          },
        }}
        actions={
          me.role === 'admin' || me.role === 'superadmin'
            ? (user) => {
                return [
                  ...(user.id !== me.id
                    ? [
                        {
                          label: t('user.options.edit'),
                          icon: <IconEdit />,
                          onSelect: editUserModal,
                        },
                      ]
                    : [
                        {
                          label: t('user.options.edit'),
                          icon: <IconEdit />,
                          onSelect: () => navigate('/profile/details'),
                        },
                      ]),
                  ...(!user.hasSetPassword
                    ? [
                        {
                          label: t('user.options.invite'),
                          icon: <IconSend />,
                          onSelect: sendInviteModal,
                        },
                      ]
                    : []),
                  {
                    label: t('user.options.delete'),
                    icon: <IconTrash />,
                    variant: 'danger',
                    onSelect: deleteUserModal,
                  },
                ];
              }
            : undefined
        }
      />

      <Box m={numPages >= 2 ? '24' : '0'} mb="80">
        <Pagination
          justify="right"
          count={numPages}
          selected={selectedPage}
          onSelect={selectPage}
        />
      </Box>
    </div>
  );
});

const RenderDepartments = observer(({ user }) => {
  const { t } = useStore();
  if (user.departmentsDisplay) {
    return user.departmentsDisplay;
  }
  return <Text color="purple50" font="regular14" text={t('user.list.departments.none')} />;
});

const RenderUserInfo = observer(({ user }) => {
  return (
    <Stack space={3} orientation="horizontal" align="center">
      <Avatar src={user.picture} name={user.name} size="md" />
      <span>{user.name}</span>
    </Stack>
  );
});

const RenderUserAccess = observer(({ user }) => {
  const { t, userStore } = useStore();
  const me = useAuthenticatedUser();

  if (me.role !== 'admin' && me.role !== 'superadmin') {
    return null;
  }

  if (user.role === 'superadmin') {
    return t('user.access.all');
  }

  if (userStore.followerStatsState !== 'loaded') {
    return t('user.access.loading');
  }

  if (!user.jobFollowCount && !user.candidateFollowCount) {
    return t('user.access.sharingonly');
  }

  let jobs = null;
  let applications = null;
  if (user.jobFollowCount) {
    jobs = (
      <div>
        {user.jobFollowCount}{' '}
        {t(`user.access.jobs.${user.jobFollowCount === 1 ? 'singular' : 'plural'}`)}
      </div>
    );
  }
  if (user.candidateFollowCount) {
    applications = (
      <div>
        {user.candidateFollowCount}{' '}
        {t(`user.access.apps.${user.candidateFollowCount === 1 ? 'singular' : 'plural'}`)}
      </div>
    );
  }
  return (
    <div>
      {jobs}
      {applications}
    </div>
  );
});

export const UserStatus = styled(Stack).attrs({
  align: 'center',
  orientation: 'horizontal',
})((p) => ({
  color:
    p.status === 'joined'
      ? theme.color.green200
      : p.status === 'invited'
      ? theme.color.blue100
      : theme.color.orange100,
}));

const getDefaultSettings = () => ({
  name: { visible: true, order: 0 },
  email: { visible: true, order: 1 },
  department: { visible: false, order: 2 },
  roleDisplay: { visible: true, order: 3 },
  access: { visible: true, order: 4 },
  status: { visible: true, order: 5 },
});

const getDefaultSort = () => ({
  key: 'name',
  order: 'ascending',
});
